import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';
import { CompanyVendor } from 'src/app/classes/models';
import { InsuranceAgent } from 'src/app/classes/models/insurance-agent';
import { TicketType } from 'src/app/interfaces/ticket-type';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { ZendeskTicketService } from 'src/app/services/zendesk-ticket.service';

@Component({
  selector: 'app-create-ticket-modal',
  templateUrl: './create-ticket-modal.component.html',
  styleUrls: ['./create-ticket-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CreateTicketModalComponent implements OnInit {
  constructor(
    private ActiveModal: NgbActiveModal,
    private ToastService: ToastService,
    private CompanyVendorService: CompanyVendorService,
    private ZendeskTicketService: ZendeskTicketService,
    private UtilityService: UtilityService
  ) {}

  CompanyId!: number;
  VendorId!: number;
  InsuranceAgentId?: number;
  InsuranceAgent?: InsuranceAgent;
  CompanyVendor?: CompanyVendor;

  @Input() subject: string = '';
  @Input() description: string = '';
  // Can generate various tickets based on type.
  @Input() ticketType: TicketType = 'default';

  TicketForm = new FormGroup({
    VendorId: new FormControl<number | undefined>(
      { value: undefined, disabled: false },
      { nonNullable: true, validators: [Validators.required] }
    ),
    CompanyId: new FormControl<number | undefined>(
      { value: undefined, disabled: false },
      { nonNullable: true, validators: [Validators.required] }
    ),
    InsuranceAgentId: new FormControl<number | undefined>(
      { value: undefined, disabled: false },
      { nonNullable: true }
    ),
    subject: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    description: new FormControl<string>('', { nonNullable: true }),
    ticketType: new FormControl<TicketType>(this.ticketType, {
      nonNullable: true,
      validators: [Validators.required]
    })
  });

  create() {
    try {
      const ticketErrors = this.UtilityService.generateValidationErrorsObject(
        this.TicketForm
      );

      if (ticketErrors) {
        throw this.ToastService.addErrorToast(
          'Error',
          'There is an error with a field below.',
          { returnError: true }
        );
      }

      const ticketInfo = this.TicketForm.value;
      const {
        VendorId,
        CompanyId,
        InsuranceAgentId,
        subject,
        description,
        ticketType
      } = ticketInfo;

      if (!VendorId) {
        throw this.ToastService.addErrorToast(
          'Error',
          'There was an error creating the ticket. Missing Vendor Id.',
          { returnError: true }
        );
      }

      if (!CompanyId) {
        throw this.ToastService.addErrorToast(
          'Error',
          'There was an error creating the ticket. Missing Company Id.',
          { returnError: true }
        );
      }

      if (
        ticketType &&
        ['newInsurance', 'expiringInsurance'].includes(ticketType) &&
        !InsuranceAgentId
      ) {
        throw this.ToastService.addErrorToast(
          'Error',
          'There was an error creating the ticket. Missing Insurance Agent Id.',
          { returnError: true }
        );
      }

      if (typeof subject === 'undefined') {
        throw this.ToastService.addErrorToast(
          'Error',
          'There was an error creating the ticket. Missing subject.',
          { returnError: true }
        );
      }

      if (typeof description === 'undefined') {
        throw this.ToastService.addErrorToast(
          'Error',
          'There was an error creating the ticket. Missing description.',
          { returnError: true }
        );
      }

      if (typeof ticketType === 'undefined') {
        throw this.ToastService.addErrorToast(
          'Error',
          'There was an error creating the ticket. Missing ticket type.',
          { returnError: true }
        );
      }

      this.ZendeskTicketService.createNewTicket({
        VendorId,
        CompanyId,
        InsuranceAgentId,
        subject,
        description,
        ticketType
      }).subscribe({
        next: (ZendeskResponse) => {
          const {
            ticket: { id }
          } = ZendeskResponse;
          this.ActiveModal.close(
            `https://getvived.zendesk.com/agent/tickets/${id}`
          );
        }
      });
    } catch (error) {
      this.ActiveModal.dismiss();
    }
  }

  close() {
    this.ActiveModal.dismiss();
  }

  getInsurancePolicyTypesFromAgent(InsuranceAgent: InsuranceAgent) {
    const {
      isGLProvider,
      isAutoProvider,
      isUmbrellaProvider,
      isWorkersProvider,
      isProfessionalProvider
    } = InsuranceAgent;

    return [
      { providerType: isGLProvider, policyType: 'GL' },
      { providerType: isAutoProvider, policyType: 'AUTO' },
      { providerType: isUmbrellaProvider, policyType: 'UMB' },
      { providerType: isWorkersProvider, policyType: 'WC' },
      { providerType: isProfessionalProvider, policyType: 'PL' }
    ]
      .reduce((accum, providerPolicyObject) => {
        const { providerType, policyType } = providerPolicyObject;
        if (providerType) {
          accum.push(policyType);
        }
        return accum;
      }, [] as string[])
      .join(', ');
  }

  setTicketFormData() {
    try {
      if (!this.CompanyVendor) {
        throw this.ToastService.addErrorToast(
          'Error',
          'There was an error loading Client/Vendor information.',
          { returnError: true }
        );
      }

      const { Company: company, Vendor: vendor } = this.CompanyVendor;

      if (!company) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing Client information.',
          { returnError: true }
        );
      }

      const { legalName: clientLegalName } = company;

      if (!vendor) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing Vendor information.',
          { returnError: true }
        );
      }

      const {
        legalName: vendorLegalName,
        InsuranceAgents,
        ComplianceContact
      } = vendor;

      if (!vendorLegalName) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Vendor is missing legal name.',
          { returnError: true }
        );
      }

      if (
        ['newInsurance', 'expiringInsurance'].includes(this.ticketType) &&
        (!Array.isArray(InsuranceAgents) || InsuranceAgents.length === 0) &&
        !this.InsuranceAgent
      ) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing Insurance Agent information to create Insurance ticket.',
          { returnError: true }
        );
      }

      if (!ComplianceContact) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing vendor contact.',
          { returnError: true }
        );
      }

      const { email: vendorEmail } = ComplianceContact;

      if (!vendorEmail) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing vendor contact email.',
          { returnError: true }
        );
      }

      const ticketTypeToSubjectDescriptionMap: {
        [key in TicketType]: () => { subject: string; description: string };
      } = {
        default: () => ({
          subject: `${vendorLegalName} Data Review Follow-up for: ${clientLegalName}`,
          description: `Follow-up for ${vendorLegalName}`
        }),
        general: () => ({
          subject: '',
          description: ''
        }),
        newInsurance: () => ({
          // Keep in mind, this doesn't display the Insurance Policies for the ticket.
          subject: `New Insurance Request ${vendorLegalName} (${this.getInsurancePolicyTypesFromAgent(this.InsuranceAgent!)}) ${clientLegalName}`,
          description: ''
        }),
        expiringInsurance: () => ({
          subject: '',
          description: ''
        }),
        wsib: () => ({
          subject: '',
          description: ''
        }),
        wcb: () => ({
          subject: '',
          description: ''
        }),
        vendorInfoReview: () => ({
          subject: `${vendorLegalName} (Business-Public Data Review Follow-up) ${clientLegalName}`,
          description: `Business-Public Data Review follow-up request sent to ${vendorEmail}`
        }),
        ownerInfoReview: () => ({
          subject: `${vendorLegalName} (Owner-Public Data Review Follow-up) ${clientLegalName}`,
          description: `Owner-Public Data Review follow-up request sent to ${vendorEmail}`
        }),
        tradeLicensesReview: () => ({
          subject: `${vendorLegalName} (Trades & Licenses Review Follow-up) ${clientLegalName}`,
          description: `Trades & Licenses Review follow-up request sent to ${vendorEmail}`
        }),
        insuranceReview: () => ({
          subject: `${vendorLegalName} (Insurance Review Follow-up) ${clientLegalName}`,
          description: `Insurance Data Review follow-up request sent to ${vendorEmail}`
        })
      };

      const { subject, description } =
        ticketTypeToSubjectDescriptionMap[this.ticketType]();

      const TicketObject = {
        VendorId: this.VendorId,
        CompanyId: this.CompanyId,
        subject: this.subject || subject,
        description: this.description || description,
        ticketType: this.ticketType
      };

      if (this.InsuranceAgentId || this.InsuranceAgent?.id) {
        Object.assign(TicketObject, {
          InsuranceAgentId: this.InsuranceAgentId || this.InsuranceAgent?.id
        });
      }

      this.TicketForm.patchValue(TicketObject);
    } catch (error) {
      this.ActiveModal.dismiss();
    }
  }

  ngOnInit(): void {
    try {
      if (!this.CompanyId) {
        throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
          returnError: true
        });
      }

      if (!this.VendorId) {
        throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
          returnError: true
        });
      }

      if (!this.CompanyVendor) {
        const CompanyVendorQuery = {
          CompanyId: this.CompanyId,
          VendorId: this.VendorId
        };
        const CompanyQuery = { excludedAttributes: ['logo'] };
        const VendorQuery = {
          excludedAttributes: ['logo'],
          ComplianceContact: {}
        };

        if (this.InsuranceAgentId) {
          Object.assign(VendorQuery, {
            options: { required: false },
            id: this.InsuranceAgentId,
            attributes: [
              'isGLProvider',
              'isAutoProvider',
              'isUmbrellaProvider',
              'isWorkersProvider',
              'isProfessionalProvider'
            ]
          });
        }

        Object.assign(CompanyVendorQuery, {
          Company: JSON.stringify(CompanyQuery),
          Vendor: JSON.stringify(VendorQuery)
        });

        this.CompanyVendorService.getCompanyVendor(
          CompanyVendorQuery
        ).subscribe({
          next: (CompanyVendor) => {
            if (!CompanyVendor) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client/Vendor information.',
                { returnError: true }
              );
            }
            this.CompanyVendor = CompanyVendor;
            this.setTicketFormData();
          }
        });
      } else {
        this.setTicketFormData();
      }
    } catch (error) {
      this.ActiveModal.dismiss();
    }
  }
}
